import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { Switch } from "@mui/material";
import { parseISO } from "date-fns";
import moment from "moment";
import { z } from "zod";

import { DateField } from "components/AddVaccineForm/components";
import { FormField } from "components/form/FormField/FormField";
import { HseDetailsNotice } from "components/ui/HseDetailsNotice/HseDetailsNotice";
import { Select } from "components/ui/Select/Select";
import { useAddSocialWelfareECert } from "features/healthlink/hooks/useAddSocialWelfareECert";
import { useHealthlinkCredentials } from "features/healthlink/hooks/useHealthlinkCredentials";
import { useHealthlinkPractice } from "features/healthlink/hooks/useHealthlinkPractice";
import { getMissingHseDetails } from "features/healthlink/utils/hsePatientReadiness";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useICD10Codes } from "hooks/useICD10Codes";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { pathGenerators } from "utils/routes";

import style from "./SocialWelfareECertForm.module.css";

const DURATION_WEEKS_OPTIONS = [
  { label: "4 weeks", value: "FOUR_WEEKS" },
  { label: "13 weeks", value: "THIRTEEN_WEEKS" },
  { label: "26 weeks", value: "TWENTY_SIX_WEEKS" },
];

const formSchema = z
  .object({
    certificateType: z
      .string()
      .min(1, { message: "Please select certificate type." }),
    natureOfCondition: z.string().optional(),
    natureOfConditionCode: z.string().optional(),
    physicalEffort: z.string().optional(),
    hospitalised: z.string().min(1, { message: "Please enter hospitalised." }),
    sickFromDate: z.string().min(1, { message: "Please provide from date." }),
    sickToDate: z.string().optional(),
    durationWeeks: z.string().optional(),
    isFinal: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (
        data.certificateType !== "MED2" &&
        data.sickFromDate &&
        data.sickToDate
      ) {
        return data.sickToDate >= data.sickFromDate;
      }

      return true;
    },
    {
      message: "Sick to date cannot be before sick from date.",
      path: ["sickToDate"],
    }
  )
  .refine(
    (data) => {
      if (data.certificateType === "MED2") {
        return !!data.durationWeeks;
      }

      return true;
    },
    {
      message: "Please select a duration.",
      path: ["durationWeeks"],
    }
  )
  // Nature of Condition Code is mandatory for Med 1 (KIN-1378). The field is
  // only shown for Med 1 (hidden for MED2), so the requirement is gated on the
  // certificate type to avoid blocking MED2 submissions.
  .refine(
    (data) =>
      data.certificateType === "MED2" || !!data.natureOfConditionCode?.trim(),
    {
      message: "Please select a nature of condition code.",
      path: ["natureOfConditionCode"],
    }
  );

export const SocialWelfareECertForm = ({
  onSuccess,
  initialData,
  consultation,
  patient,
}) => {
  const { data: staffInfo } = useStaffInfo();
  const { data: healthlinkPractice } = useHealthlinkPractice();
  const { data: healthlinkCredentials } = useHealthlinkCredentials();
  const currentPatientId = useCurrentPatientId();

  const hasProfessionalMedicalNumber =
    !!staffInfo?.staffMember?.professionalMedicalNumber;
  const hasDeaspPanelNumber = !!staffInfo?.staffMember?.deaspPanelNumber;
  const hasHealthlinkConfig =
    !!healthlinkPractice &&
    Array.isArray(healthlinkCredentials) &&
    healthlinkCredentials.length > 0;
  // DEASPSickCertBuilder sets PID.7 from the date of birth and PID.8 from
  // `patient.getGender().getGender()` — the latter NPEs on a null gender, so the
  // certificate cannot be built for a patient missing either (KIN-1733).
  const missingHseDetails = getMissingHseDetails(patient);
  const canSave =
    hasProfessionalMedicalNumber &&
    hasDeaspPanelNumber &&
    hasHealthlinkConfig &&
    missingHseDetails.length === 0;

  const comboboxRef = useRef(null);
  const [search, setSearch] = useState(
    initialData?.natureOfConditionCode || ""
  );
  const [isOpen, setIsOpen] = useState(false);

  const { data: icd10Codes, isLoading } = useICD10Codes(search);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      certificateType: initialData?.certificateType || "MED1",
      natureOfCondition: initialData?.natureOfCondition || "",
      natureOfConditionCode: initialData?.natureOfConditionCode || "",
      sickFromDate: initialData?.sickFromDate
        ? moment(initialData.sickFromDate, "YYYY-MM-DD HH:mm").format(
            "YYYY-MM-DD"
          )
        : moment().format("YYYY-MM-DD"),
      sickToDate: initialData?.sickToDate
        ? moment(initialData.sickToDate, "YYYY-MM-DD HH:mm").format(
            "YYYY-MM-DD"
          )
        : moment().format("YYYY-MM-DD"),
      physicalEffort: initialData?.physicalEffort || "LIGHT",
      hospitalised: initialData?.hospitalised || "false",
      durationWeeks: initialData?.durationWeeks
        ? String(initialData.durationWeeks)
        : "",
      isFinal: initialData?.isFinal || false,
    },
  });

  const certificateType = form.watch("certificateType");
  const isMed2 = certificateType === "MED2";

  const { mutate: addSocialWelfareECert, isPending } = useAddSocialWelfareECert(
    {
      onSuccess: () => {
        onSuccess?.();
      },
    }
  );

  const handleICDSelect = ({ code, name, icdType }) => {
    form.setValue("natureOfConditionCode", code, { shouldValidate: true });

    const conditionDetails = `(${icdType}) - ${name}`;
    form.setValue("natureOfCondition", conditionDetails, {
      shouldValidate: true,
    });
    setIsOpen(false);
    setSearch(code);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setIsOpen(true);
    form.setValue("natureOfConditionCode", value, { shouldValidate: true });
    form.setValue("natureOfCondition", "");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [comboboxRef]);

  const renderDropdownContent = () => {
    if (search.length < 3) {
      return (
        <div style={{ padding: "12px", color: "#666", fontSize: "14px" }}>
          Type at least 3 characters to search for ICD codes
        </div>
      );
    }

    if (isLoading) {
      return (
        <div style={{ padding: "12px", color: "#666", fontSize: "14px" }}>
          Loading...
        </div>
      );
    }

    if (!icd10Codes?.length) {
      return (
        <div style={{ padding: "12px" }}>
          <div style={{ color: "#666", fontSize: "14px", marginBottom: "4px" }}>
            No ICD codes found for "{search}"
          </div>
          <div style={{ color: "#999", fontSize: "12px" }}>
            Try searching with a different term
          </div>
        </div>
      );
    }

    return icd10Codes.map((code) => (
      <div
        key={code.id}
        style={{
          padding: "12px",
          borderBottom: "1px solid #f0f0f0",
          cursor: "pointer",
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
        onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
        onClick={() => handleICDSelect(code)}
      >
        <div style={{ fontWeight: "500", fontSize: "14px", color: "#333" }}>
          {code.code}
        </div>
        <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
          <span style={{ marginRight: "12px" }}>ICD Code: {code.icdType}</span>
          <span>Name: {code.name}</span>
        </div>
      </div>
    ));
  };

  const handleFormSubmit = async (values) => {
    const patientId = patient?.id || currentPatientId;

    if (!patientId) {
      console.error("No patient ID available for DEASP form submission");
      return;
    }

    const isMed2Form = values.certificateType === "MED2";

    addSocialWelfareECert({
      patientId: parseInt(patientId, 10),
      consultationDateTime: consultation?.startTime
        ? parseISO(consultation.startTime.slice(0, 16)).toISOString()
        : new Date().toISOString(),
      certificateType: values.certificateType,
      sickFromDate: values.sickFromDate,
      ...(isMed2Form
        ? { durationWeeks: values.durationWeeks }
        : { sickToDate: values.sickToDate }),
      natureOfConditionCode: values.natureOfConditionCode,
      physicalEffort: values.physicalEffort,
      hospitalized: values.hospitalised === "true",
      final: values.isFinal,
    });
  };

  return (
    <div style={{ width: "100%" }}>
      <h2
        style={{
          borderBottom: "1px solid var(--gray-200)",
          paddingBottom: "12px",
          marginBottom: "16px",
          fontSize: "20px",
          lineHeight: "32px",
        }}
      >
        Social Welfare E-Cert
      </h2>
      <HseDetailsNotice
        patient={patient}
        patientId={patient?.id ?? currentPatientId}
        submissionName="to the DEASP"
      />
      {!hasHealthlinkConfig && (
        <div className={style.missingMedicalNumberAlert}>
          You cannot send this form without Healthlink configuration set. Please
          add your Healthlink details in{" "}
          <Link
            to={pathGenerators.settings.healthlink()}
            className={style.alertLink}
          >
            Healthlink Settings
          </Link>
          .
        </div>
      )}
      {(!hasProfessionalMedicalNumber || !hasDeaspPanelNumber) && (
        <div className={style.missingMedicalNumberAlert}>
          You cannot send this form without{" "}
          <strong>
            {[
              !hasProfessionalMedicalNumber && "Medical Council Number",
              !hasDeaspPanelNumber && "DEASP Panel Number",
            ]
              .filter(Boolean)
              .join(", ")}
          </strong>{" "}
          set. Please add it in{" "}
          <Link
            to={pathGenerators.settings.profile()}
            className={style.alertLink}
          >
            Profile Settings
          </Link>
          .
        </div>
      )}
      <FormProvider {...form}>
        <form
          className={style.addLetterForm}
          onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("SocialWelfareECertForm"))}
        >
          <div className={style.inputGroupGrid3}>
            <FormField name="certificateType">
              <label className={style.addLetterFormLabel}>
                Certificate Type
              </label>
              <Select
                name="certificateType"
                options={[
                  { label: "Medical Certificate 1", value: "MED1" },
                  { label: "Medical Certificate 2", value: "MED2" },
                ]}
                value={form.watch("certificateType")}
                onChange={(value) => form.setValue("certificateType", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
            <FormField name="physicalEffort">
              <label className={style.addLetterFormLabel}>
                Physical Effort (optional)
              </label>
              <Select
                name="physicalEffort"
                options={[
                  { label: "Light", value: "LIGHT" },
                  { label: "Moderate", value: "MODERATE" },
                  { label: "Heavy", value: "HEAVY" },
                  { label: "Not Applicable", value: "NOT_APPLICABLE" },
                ]}
                value={form.watch("physicalEffort")}
                onChange={(value) => form.setValue("physicalEffort", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
            <FormField name="hospitalised">
              <label className={style.addLetterFormLabel}>Hospitalised</label>
              <Select
                name="hospitalised"
                options={[
                  { label: "Yes", value: "true" },
                  { label: "No", value: "false" },
                ]}
                value={form.watch("hospitalised")}
                onChange={(value) => form.setValue("hospitalised", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
          </div>
          {!isMed2 && (
            <div className={style.inputGroupGrid}>
              <div ref={comboboxRef} style={{ position: "relative" }}>
                <FormField name="natureOfConditionCode">
                  <label className={style.addLetterFormLabel}>
                    Nature of Condition Code
                  </label>
                  <div className={style.cmInput}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search for ICD codes..."
                      onChange={handleInputChange}
                      onFocus={() => setIsOpen(true)}
                      value={search}
                    />
                  </div>
                </FormField>
                {isOpen && (
                  <div
                    className={style.dropdown}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 1000,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {renderDropdownContent()}
                  </div>
                )}
              </div>

              <FormField name="natureOfCondition">
                <label className={style.addLetterFormLabel}>
                  Nature of Condition
                </label>
                <div className={style.cmInput}>
                  <input
                    type="text"
                    className={`form-control ${style.readonlyInput}`}
                    readOnly
                    {...form.register("natureOfCondition")}
                  />
                </div>
              </FormField>
            </div>
          )}
          <div className={style.inputGroupGrid}>
            <div>
              <DateField name="sickFromDate" label="Sick from date" />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: "20px",
                  gap: "4px",
                }}
              >
                <Switch
                  checked={form.watch("isFinal")}
                  onChange={(e) => form.setValue("isFinal", e.target.checked)}
                  sx={{
                    padding: "6px",
                    marginLeft: "-6px",
                    borderRadius: "16px",
                    "& .MuiSwitch-switchBase": {
                      "&.Mui-checked": {
                        color: "#fff",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "var(--green-800)",
                          opacity: 1,
                        },
                      },
                    },
                    "& .MuiSwitch-thumb": {
                      backgroundColor: "#fff",
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#a0a0a0",
                      opacity: 0.5,
                      padding: 0,
                      borderRadius: "16px",
                    },
                  }}
                />
                <span style={{ fontSize: "15px", marginTop: "2px" }}>
                  is Final
                </span>
              </div>
            </div>
            {isMed2 ? (
              <FormField name="durationWeeks">
                <label className={style.addLetterFormLabel}>Duration</label>
                <Select
                  name="durationWeeks"
                  options={DURATION_WEEKS_OPTIONS}
                  value={form.watch("durationWeeks")}
                  onChange={(value) =>
                    form.setValue("durationWeeks", value, {
                      shouldValidate: true,
                    })
                  }
                  placeholder="Select duration"
                  fullWidth
                />
              </FormField>
            ) : (
              <DateField name="sickToDate" label="Sick to date" />
            )}
          </div>
          <div className={style.bottomSection}>
            <div>
              <button
                className={style.sendButton}
                disabled={
                  isPending ||
                  !canSave ||
                  Object.keys(form.formState.errors).length > 0
                }
                onClick={form.handleSubmit(handleFormSubmit, onInvalidSubmit("SocialWelfareECertForm"))}
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
